import { columns } from "@/components/documents/documents.columns";
import { DataTable } from "@/components/table/table";
import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useDocumentsModal } from "@/hooks/useDocumentsModal";
import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../ui/button";
import { ConfirmModal } from "../ui/confirm-modal";
import { DocumentsModalForm } from "./documents-modal-form";

const documentFormSchema = z.object({
  documentType: z.enum(["ordinance", "proclamation"]),
  number: z.string().optional(),
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  date: z.date(),
  status: z.enum(["current", "archived", "proposed"]).default("current"),
  category: z.string().optional(),
  documentMediaId: z.string().optional(),
});

type DocumentFormValues = z.infer<typeof documentFormSchema>;

export function DocumentsTable() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { data: documents = [], isLoading } = useQuery(
    trpc.documents.list.queryOptions(),
  );

  const deleteDocumentId = useDeleteEvent((s) => s.toDeleteId);
  const setDeleteDocumentId = useDeleteEvent((s) => s.setToDeleteId);

  const isDocumentModalOpen = useDocumentsModal((s) => s.isOpen);
  const setIsDocumentModalOpen = useDocumentsModal((s) => s.setIsModalOpen);
  const setDocumentId = useDocumentsModal((s) => s.setDocumentId);
  const documentId = useDocumentsModal((s) => s.documentId);
  const defaultValues = useDocumentsModal((s) => s.defaultValues);
  const setDefaultValues = useDocumentsModal((s) => s.setDefaultValues);

  const form = useForm<DocumentFormValues>({
    resolver: zodResolver(documentFormSchema),
    defaultValues: {
      documentType: "ordinance",
      number: "",
      title: "",
      description: "",
      date: new Date(),
      status: "current",
      category: "",
      documentMediaId: undefined,
    },
  });

  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      form.reset({
        documentType: defaultValues.documentType || "ordinance",
        number: defaultValues.number || "",
        title: defaultValues.title || "",
        description: defaultValues.description || "",
        date: defaultValues.date || new Date(),
        status: defaultValues.status || "current",
        category: defaultValues.category || "",
        documentMediaId: defaultValues.documentMediaId,
      });
    }
  }, [form, defaultValues]);

  const deleteMutation = useMutation(
    trpc.documents.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["documents"] });
        setDeleteDocumentId(null);
        toast.success("Document deleted successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to delete document");
      },
    }),
  );

  const createMutation = useMutation(
    trpc.documents.create.mutationOptions({
      onSuccess: () => {
        form.reset();
        queryClient.invalidateQueries({ queryKey: ["documents"] });
        setDocumentId(null);
        toast.success("Document created successfully");
        setIsDocumentModalOpen(false);
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to create document");
      },
    }),
  );

  const updateMutation = useMutation(
    trpc.documents.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["documents"] });
        setDocumentId(null);
        setIsDocumentModalOpen(false);
        form.reset();
        toast.success("Document updated successfully");
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to update document");
      },
    }),
  );

  const handleSubmit = (data: DocumentFormValues) => {
    if (documentId) {
      updateMutation.mutate({ ...data, id: documentId });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="flex flex-1 p-8 justify-around gap-8">
      <div className="flex flex-col space-y-8 w-full xl:w-3/4 lg:max-w-4xl xl:max-w-6xl">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              Ordinances & Proclamations
            </h2>
            <p className="text-muted-foreground">
              Manage official documents for the website
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => {
                setDefaultValues({});
                form.reset({
                  documentType: "ordinance",
                  number: "",
                  title: "",
                  description: "",
                  date: new Date(),
                  status: "current",
                  category: "",
                  documentMediaId: undefined,
                });
                setIsDocumentModalOpen(true);
              }}
            >
              New Document
            </Button>
          </div>
        </div>

        <DocumentsModalForm
          isModalOpen={isDocumentModalOpen}
          setIsModalOpen={setIsDocumentModalOpen}
          form={form}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />

        <ConfirmModal
          open={Boolean(deleteDocumentId)}
          onConfirm={() => {
            if (deleteDocumentId)
              deleteMutation.mutate({ id: deleteDocumentId });
          }}
          onDismiss={() => {
            setDeleteDocumentId(null);
          }}
          isLoading={deleteMutation.isPending}
        />

        {!isLoading && (
          <DataTable data={documents} columns={columns} filterColumn="title" />
        )}
      </div>
    </div>
  );
}
